/**
 * Authenticated quote/reply previews for kaether_for_web.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/QuotedReply.swift` byte-for-byte so
 * a reply encoded on iOS can be decoded on web and vice-versa.
 *
 * Signal's reply feature carries a "quoted preview" of the message being
 * replied to (author, truncated body, timestamp). A malicious server, or
 * a malicious peer relaying old chat history, must not be able to forge
 * what the quote claims an author said. We achieve that by:
 *
 *   1. Embedding a SHA256 hash of the original plaintext bytes inside the
 *      reply's *signed* plaintext. Anyone who has the original message
 *      can recompute the hash and detect tampering.
 *   2. Treating `quotedAuthorId` as advisory — the verifier recomputes
 *      the actual author from local storage. A reply that claims to
 *      quote "Bob" but whose hash matches a message authored by "Carol"
 *      surfaces as `authorMismatch`.
 *   3. Re-deriving the rendered preview from the local copy at render
 *      time when available. The wire `quotedPreview` is only a hint
 *      shown when the receiver does not have the original cached
 *      (joined late, expired, etc.) and is flagged `unverified`.
 *
 * Wire format of the *plaintext that gets encrypted* (NOT the on-the-wire
 * envelope, which is the standard `kaether:e2ee:v1:` form):
 *
 *   kaether:reply:v1:<base64url(JSON)>
 *
 * JSON shape:
 *   { "schema": 1, "quotedMessageId": "abc",
 *     "quotedAuthorId": "alice", "quotedPlaintextHash": "<base64>",
 *     "quotedTs": 1700000000, "quotedPreview": "...",
 *     "replyBody": "the user-typed reply text" }
 *
 * @module QuotedReply
 */

// ---------------------------------------------------------------------------
// Value types
// ---------------------------------------------------------------------------

export interface QuotedReplyPayload {
  /** Always 1 for this version of the codec. */
  readonly schema: number;
  /** The message ID of the message being quoted. */
  readonly quotedMessageId: string;
  /**
   * Advisory only — the verifier recomputes from local storage. A hostile
   * sender can lie here; the hash check is what actually authenticates the
   * original message.
   */
  readonly quotedAuthorId: string;
  /**
   * SHA256 of the original plaintext UTF-8 bytes, encoded as standard base64.
   * 32 bytes.
   */
  readonly quotedPlaintextHash: string;
  /** Unix timestamp (seconds) of the original message. */
  readonly quotedTs: number;
  /**
   * Truncated, newline-flattened preview, max 120 chars + ellipsis.
   * Untrusted — use only when the original is not available locally.
   */
  readonly quotedPreview: string;
  /**
   * The actual user-typed reply text — what the bubble below the quoted
   * preview will render.
   */
  readonly replyBody: string;
}

// ---------------------------------------------------------------------------
// Codec errors
// ---------------------------------------------------------------------------

export class QuotedReplyCodecError extends Error {
  readonly code: "NOT_A_REPLY" | "MALFORMED" | "UNSUPPORTED_SCHEMA";
  readonly detail?: string;

  constructor(
    code: "NOT_A_REPLY" | "MALFORMED" | "UNSUPPORTED_SCHEMA",
    detail?: string,
  ) {
    const message =
      code === "NOT_A_REPLY"
        ? "plaintext does not start with quoted-reply prefix"
        : code === "MALFORMED"
          ? `malformed quoted-reply payload${detail ? `: ${detail}` : ""}`
          : `unsupported schema version${detail ? `: ${detail}` : ""}`;
    super(message);
    this.name = "QuotedReplyCodecError";
    this.code = code;
    this.detail = detail;
  }
}

// ---------------------------------------------------------------------------
// Codec
// ---------------------------------------------------------------------------

/** The sentinel prefix that distinguishes reply plaintexts from all others. */
const REPLY_PREFIX = "kaether:reply:v1:" as const;

/** Current schema version — must match iOS `QuotedReplyPayload.currentSchema`. */
const CURRENT_SCHEMA = 1;

/** Max preview length in Unicode code points, matching iOS `maxPreviewLength`. */
const MAX_PREVIEW_LENGTH = 120;

/**
 * The raw JSON shape stored inside the base64url segment. The wire type uses
 * `quotedPlaintextHash` as a standard-base64 string (matching iOS's
 * `Data.base64EncodedString()` output) so cross-platform byte equality holds.
 */
interface ReplyJson {
  schema: number;
  quotedMessageId: string;
  quotedAuthorId: string;
  quotedPlaintextHash: string;
  quotedTs: number;
  quotedPreview: string;
  replyBody: string;
}

/**
 * Compute SHA256 of the UTF-8 bytes of `plaintext` and return the result as
 * standard base64. Must match iOS `QuotedReplyCodec.computeHash(plaintext:)`
 * byte-for-byte — this is the integrity anchor.
 */
async function computeHash(plaintext: string): Promise<string> {
  const bytes = new TextEncoder().encode(plaintext);
  const hashBuffer = await crypto.subtle.digest("SHA-256", bytes);
  return arrayBufferToBase64(hashBuffer);
}

/** Encode a reply payload as the plaintext string that enters the E2EE pipeline. */
async function encode(payload: QuotedReplyPayload): Promise<string> {
  const json: ReplyJson = {
    schema: payload.schema,
    quotedMessageId: payload.quotedMessageId,
    quotedAuthorId: payload.quotedAuthorId,
    quotedPlaintextHash: payload.quotedPlaintextHash,
    quotedTs: payload.quotedTs,
    quotedPreview: payload.quotedPreview,
    replyBody: payload.replyBody,
  };
  const jsonStr = JSON.stringify(json);
  const b64url = stringToBase64url(jsonStr);
  return REPLY_PREFIX + b64url;
}

/**
 * Decode a reply plaintext into a `QuotedReplyPayload`.
 * Throws `QuotedReplyCodecError` if the plaintext is not a reply or is invalid.
 */
function decode(plaintext: string): QuotedReplyPayload {
  if (!plaintext.startsWith(REPLY_PREFIX)) {
    throw new QuotedReplyCodecError("NOT_A_REPLY");
  }
  const b64url = plaintext.slice(REPLY_PREFIX.length);
  let jsonStr: string;
  try {
    jsonStr = base64urlToString(b64url);
  } catch {
    throw new QuotedReplyCodecError("MALFORMED", "invalid base64url");
  }

  let json: ReplyJson;
  try {
    json = JSON.parse(jsonStr) as ReplyJson;
  } catch (e) {
    throw new QuotedReplyCodecError(
      "MALFORMED",
      `json decode failed: ${e instanceof Error ? e.message : String(e)}`,
    );
  }

  if (json.schema !== CURRENT_SCHEMA) {
    throw new QuotedReplyCodecError(
      "UNSUPPORTED_SCHEMA",
      String(json.schema),
    );
  }
  if (!json.quotedMessageId) {
    throw new QuotedReplyCodecError("MALFORMED", "empty quotedMessageId");
  }
  if (!json.quotedAuthorId) {
    throw new QuotedReplyCodecError("MALFORMED", "empty quotedAuthorId");
  }
  // Validate hash is 32 bytes (256 bits = 44 base64 chars with padding, 43 without).
  const hashBytes = tryDecodeBase64(json.quotedPlaintextHash);
  if (hashBytes === null || hashBytes.byteLength !== 32) {
    throw new QuotedReplyCodecError(
      "MALFORMED",
      "quotedPlaintextHash must be 32 bytes (SHA256)",
    );
  }

  return {
    schema: json.schema,
    quotedMessageId: json.quotedMessageId,
    quotedAuthorId: json.quotedAuthorId,
    quotedPlaintextHash: json.quotedPlaintextHash,
    quotedTs: json.quotedTs,
    quotedPreview: json.quotedPreview,
    replyBody: json.replyBody,
  };
}

/**
 * Returns `null` for non-replies (no prefix). Throws `QuotedReplyCodecError`
 * only for plaintext strings that *did* claim to be replies (prefix present)
 * but are malformed.
 */
function tryDecode(plaintext: string): QuotedReplyPayload | null {
  if (!plaintext.startsWith(REPLY_PREFIX)) return null;
  return decode(plaintext);
}

/** Returns `true` iff `plaintext` starts with the reply sentinel prefix. */
function isReply(plaintext: string): boolean {
  return plaintext.startsWith(REPLY_PREFIX);
}

/**
 * Produce a newline-flattened, truncated preview of `plaintext`. Used by the
 * sender when building a `QuotedReplyPayload`. Receiver should prefer the
 * local-stored original whenever available.
 *
 * `maxLength` is measured in Unicode code points (grapheme clusters count as
 * one, same as iOS's `String.count`). If the flattened text exceeds
 * `maxLength`, it is cut at that boundary and an ellipsis (U+2026) appended.
 */
function preview(
  plaintext: string,
  maxLength: number = MAX_PREVIEW_LENGTH,
): string {
  // Replace all newline variants with a space — same order as iOS.
  const flattened = plaintext
    .replace(/\r\n/g, " ")
    .replace(/\n/g, " ")
    .replace(/\r/g, " ");
  // Spread to Unicode code points so multi-byte emoji count as one.
  const codePoints = [...flattened];
  if (codePoints.length <= maxLength) {
    return flattened;
  }
  return codePoints.slice(0, maxLength).join("") + "…";
}

/**
 * Build a `QuotedReplyPayload` from the message being quoted. This is the
 * canonical sender-side constructor. The `quotedPlaintextHash` is computed
 * automatically from `originalPlaintext`.
 */
async function buildPayload(opts: {
  quotedMessageId: string;
  quotedAuthorId: string;
  originalPlaintext: string;
  quotedTs: number;
  replyBody: string;
}): Promise<QuotedReplyPayload> {
  const hash = await computeHash(opts.originalPlaintext);
  return {
    schema: CURRENT_SCHEMA,
    quotedMessageId: opts.quotedMessageId,
    quotedAuthorId: opts.quotedAuthorId,
    quotedPlaintextHash: hash,
    quotedTs: opts.quotedTs,
    quotedPreview: preview(opts.originalPlaintext),
    replyBody: opts.replyBody,
  };
}

// ---------------------------------------------------------------------------
// Public API surface
// ---------------------------------------------------------------------------

export const QuotedReplyCodec = {
  /** The sentinel prefix `kaether:reply:v1:`. */
  prefix: REPLY_PREFIX,

  /** Current schema version (1). */
  currentSchema: CURRENT_SCHEMA,

  /** Max preview length in Unicode code points (120). */
  maxPreviewLength: MAX_PREVIEW_LENGTH,

  isReply,
  encode,
  decode,
  tryDecode,
  preview,
  buildPayload,

  /**
   * Compute SHA256 of the UTF-8 bytes of `plaintext`, returned as standard
   * base64. Sender and receiver must use this exact routine so byte-equality
   * holds across platforms — the hash is the integrity anchor for the quote.
   */
  computeHash,
} as const;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Encode a JSON string as base64url (no padding), matching iOS's encoding. */
function stringToBase64url(s: string): string {
  // TextEncoder → Uint8Array → base64 → base64url
  const bytes = new TextEncoder().encode(s);
  return uint8ArrayToBase64url(bytes);
}

/** Decode a base64url string to a UTF-8 string. Throws on invalid input. */
function base64urlToString(s: string): string {
  const bytes = base64urlToUint8Array(s);
  return new TextDecoder().decode(bytes);
}

/** Convert an ArrayBuffer to standard (not url-safe) base64 with padding. */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary);
}

/** Encode Uint8Array as base64url (no padding). */
function uint8ArrayToBase64url(bytes: Uint8Array): string {
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
}

/** Decode base64url (with or without padding) to Uint8Array. */
function base64urlToUint8Array(s: string): Uint8Array {
  const padded = s.replace(/-/g, "+").replace(/_/g, "/");
  const padLen = (4 - (padded.length % 4)) % 4;
  const b64 = padded + "=".repeat(padLen);
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/**
 * Attempt to decode a standard base64 string and return its byte length.
 * Returns `null` if the string is not valid base64.
 */
function tryDecodeBase64(s: string): Uint8Array | null {
  try {
    const padLen = (4 - (s.length % 4)) % 4;
    const b64 = s + "=".repeat(padLen);
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
  } catch {
    return null;
  }
}
